import React from 'react'
import { match } from 'ts-pattern'
import type { primitives } from '@adc/blueprint'
import classnames from 'classnames/bind'
import { FeatureFlags, useListingContext } from 'common/context/listing'
import * as AuctionDuration from 'common/modules/bidBanner/auctionDuration'
import FAQSection from 'common/modules/bidBanner/faqSection'
import {
  CountdownDuring,
  getEndTimeMoment,
  getServerTimeMoment,
} from 'common/modules/auctionCountdown'
import * as Header from 'common/modules/tpsOnlineBidBanner/header'
import * as BidDetails from 'common/modules/bidBanner/bidDetails'
import {
  Container,
  LeftMarginSection,
} from 'common/modules/bidBanner/container'
import BidForm from 'common/modules/bidBanner/bidForm'
import BidFormUI from 'common/modules/bidBanner/bidForm/ui'
import * as graph from '@adc/sdk'

// TODO: Make this typescript

const cx = classnames.bind({})

const renderRightSide = (props: any) => {
  const { segment } = useListingContext()

  return (
    <>
      <div className={cx('u-mb-6')}>
        <CountdownDuring
          endTime={getEndTimeMoment(segment)}
          currentServerTime={getServerTimeMoment(segment)}
          segment={segment}
        />
      </div>
      <BidForm {...props}>
        {(bidFormContainerProps: any) => (
          <BidFormUI
            {...bidFormContainerProps}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            featureFlags={props.featureFlags}
          />
        )}
      </BidForm>
    </>
  )
}

const NoBids = (props: any) => {
  const { listing, segment } = useListingContext()

  const { nextBidAmount } = segment.__methods__.calculateAuctionValues(listing)
  return (
    <Container
      ribbonColor='adcLightBlue'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment?.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.NoBids nextBidAmount={nextBidAmount} />
          </div>
          <LeftMarginSection>
            <BidDetails.During segment={segment} listing={listing} />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

const BidPlacedPending = (props: any) => {
  const { listing, segment } = useListingContext()
  return (
    <Container
      ribbonColor='success'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.BidPlacedPending />
          </div>
          <LeftMarginSection>
            <BidDetails.During segment={segment} listing={listing} />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

const HighestBidReserveNotMet = (props: any) => {
  const { listing, segment } = useListingContext()
  return (
    <Container
      ribbonColor='success'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.BidPlacedNotMet />
          </div>
          <LeftMarginSection>
            <BidDetails.During segment={segment} listing={listing} />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

const HighestBidReserveMet = (props: any) => {
  const { listing, segment } = useListingContext()
  return (
    <Container
      ribbonColor='success'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.BidPlacedMet />
          </div>
          <LeftMarginSection>
            <BidDetails.During segment={segment} listing={listing} />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

const Outbid = (props: any) => {
  const { listing, segment } = useListingContext()
  return (
    <Container
      ribbonColor='caution'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.Outbid />
          </div>
          <LeftMarginSection>
            <BidDetails.During segment={segment} listing={listing} />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

interface DuringProps {
  featureFlags: FeatureFlags
  performBidVerificationAction: (bidVerificationAction: any) => void
  user: primitives.AuctionUser
}

const During = (props: DuringProps) => {
  const { segment } = useListingContext()
  const status = {
    current_user_bidding_status: segment?.current_user_bidding_status,
    reserve_status: segment?.reserve_status,
  }
  return match(status)
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.NoBid,
      },
      () => <NoBids {...props} />
    )
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.HighestBid,
        reserve_status: graph.enums.ReserveStatus.ReserveNotSet,
      },
      () => <BidPlacedPending {...props} />
    )
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.HighestBid,
        reserve_status: graph.enums.ReserveStatus.ReserveMet,
      },
      () => <HighestBidReserveMet {...props} />
    )
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.HighestBid,
        reserve_status: graph.enums.ReserveStatus.ReserveNotMet,
      },
      () => <HighestBidReserveNotMet {...props} />
    )
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.Outbid,
      },
      () => <Outbid {...props} />
    )
    .otherwise(() => (
      <Container
        left={
          <>
            <div>During Auction</div>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </>
        }
        right={renderRightSide(props)}
      />
    ))
}

export default During
